import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import useStore from '../../store/useStore';
import { metricsAPI } from '../../services/api';
import ReportDisplay from './ReportDisplay';
import useModalOverlayClose from '../../hooks/useModalOverlayClose';
import { downloadCsv } from '../../utils/reportExport';

const HISTORY_LIMIT = 200;

/** Drill from a live widget: the recent history of its pushed values. */
const MetricHistoryModal = ({ metricKey, title, onClose }) => {
  const currentCompany = useStore((state) => state.currentCompany);
  const overlay = useModalOverlayClose(onClose);
  const [state, setState] = useState({ loading: true, error: null, rows: [] });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await metricsAPI.getHistory(metricKey, { companyId: currentCompany?.id, limit: HISTORY_LIMIT });
        if (cancelled) return;
        if (!res?.success) throw new Error(res?.error || 'Failed to load history');
        const rows = (res.data || [])
          .map((m) => {
            const v = m.value && typeof m.value === 'object' ? m.value.current : m.value;
            const n = Number(v);
            return { time: new Date(m.timestamp).toLocaleString([], { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }), value: Number.isNaN(n) ? null : n, at: m.timestamp };
          })
          .filter((r) => r.value !== null)
          .sort((a, b) => new Date(a.at) - new Date(b.at))
          .map(({ time, value }) => ({ time, value }));
        setState({ loading: false, error: null, rows });
      } catch (err) {
        if (!cancelled) setState({ loading: false, error: err.response?.data?.error || err.message, rows: [] });
      }
    })();
    return () => { cancelled = true; };
  }, [metricKey, currentCompany?.id]);

  // Rendered on the page body: a widget's grid position would otherwise confine the overlay to the widget.
  return createPortal(
    <div className="modal-overlay" {...overlay}>
      <div className="modal-content drill-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{title}: recent history</h2>
          <button onClick={onClose} className="modal-close" aria-label="Close">&times;</button>
        </div>
        <div className="drill-body widget report-widget">
          <div className="widget-body report-body">
            {state.loading ? <div className="report-status">Loading…</div>
              : state.error ? <div className="report-status report-status--error">⚠ {state.error}</div>
                : state.rows.length < 2 ? <div className="report-status">Not enough history yet.</div>
                  : <ReportDisplay rows={state.rows} config={{ displayType: 'line' }} />}
          </div>
        </div>
        <div className="form-actions">
          <span className="dataset-empty">{state.rows.length} point{state.rows.length === 1 ? '' : 's'} (last {HISTORY_LIMIT} pushes)</span>
          <button className="btn-secondary" disabled={!state.rows.length} onClick={() => downloadCsv(state.rows, `${title} history`)}>Download CSV</button>
          <button className="btn-primary" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default MetricHistoryModal;
